import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { GetCouponQuery as GetCoupon } from '__generated__/graphql';
import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import GenerateCouponCodesMutation from './GenerateCouponCodesMutation';
import UI from '../../../../common/UI';
import useIntegerInput from '../../../../common/useIntegerInput';
import useMutation from '../../../../api/useMutation';
import useValidation from '../../../../common/useValidation';

type Coupon = GetCoupon['coupon'];

interface GenerateCouponCodesFormProps {
  coupon: Coupon;
  onSuccess?: () => void;
}

interface GenerateCouponCodesData {
  count: number;
}

const generateCouponCodesSchema = yup.object().shape({
  count: yup.number().min(1).max(2500),
});

const generateExampleSuffix = () => {
  let result = '';
  const tokens = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  for (let i = 0; i < 8; i++) {
    result += tokens.charAt(Math.floor(Math.random() * tokens.length));
  }
  return result;
};

const GenerateCouponCodesForm = ({ coupon, onSuccess }: GenerateCouponCodesFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const integerInput = useIntegerInput();

  const form = useForm<GenerateCouponCodesData>({
    values: {
      count: null,
    },
  });

  const [generateCouponCodes, { loading, error }] = useMutation(
    GenerateCouponCodesMutation,
    {
      variables: {
        input: {
          coupon: { id: coupon.id },
          ...form.values,
        },
      },
      onCompleted: () => {
        onSuccess?.();
        notifier.success(t('notifications.coupon_codes_generated'));
      },
      onError: () => {
        //
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: generateCouponCodesSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const suffixRef = useRef<string>();
  if (!suffixRef.current) {
    suffixRef.current = generateExampleSuffix();
  }

  return (
    <UI.Form onSubmit={() => generateCouponCodes()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('generate_coupon_codes')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t(coupon.codes_count === 0
              ? 'number_of_coupon_codes_description'
              : 'number_of_new_coupon_codes_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.count, touched: form.touched.count }}>
          <UI.InputLabel>
            {t(coupon.codes_count === 0
              ? 'number_of_coupon_codes'
              : 'number_of_new_coupon_codes')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text('count', { inputHandler: integerInput() })}
            maxLength={4}
            format={integerInput().format}
            inputMode="numeric"
            match={/[\d]/g}
          />
          <UI.ErrorMessages attribute={t('number_of_new_coupon_codes')} errors={errors.count} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.count, touched: form.touched.count }}>
          <UI.InputLabel>
            {t('example')}
          </UI.InputLabel>
          <UI.Label>
            <UI.Strong>
              {coupon.prefix}
              {suffixRef.current}
            </UI.Strong>
          </UI.Label>
        </UI.InputGroup>

        <UI.Warning>
          {t('generate_coupon_codes_warning')}
        </UI.Warning>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('generate')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default GenerateCouponCodesForm;
